/** Kjenner igjen tall-linjer og tall-filer. Bygd på akkord.ts. Ren tekstbehandling. */
import { erBokstavakkord, erTallakkord } from './akkord';

const SKILLETEGN = /^(?:\(x\d+\)|x\d+|\||-|\/{1,3})$/;
const GJENTAKELSE = /^\(?x\d+\)?$/;
/** Ordene som kan stå som seksjon eller ledetekst. */
export const SEKSJONSORD =
  'intro|verse|vers|chorus|refreng|refrain|refr|ref|kor|bridge|bro|outro|pre-chorus|prechorus|pre-refreng|tag|coda|interlude|turnaround|ending|instrumental|mellomspill|solo|slutt';
/** Ledetekst foran akkorder: bare fast liste (uavhengig av store bokstaver), valgfritt tall, kolon (`Intro:`, `Vers 2:`). */
const LEDETEKST = new RegExp(`^(?:${SEKSJONSORD})\\s*\\d*\\s*:\\s*`, 'i');
const I_HAKE = /\[([^\]]+)\]/g;
const MINSTE_BEVIS = 3;

const linjerI = (tekst: string): string[] => tekst.split(/\r?\n/);

/** Deler en linje i ledetekst (hvis den står i fast liste) og tokens. */
function tokensUtenLedetekst(linje: string): { tokens: string[]; ledetekst: boolean } {
  const tekst = linje.trim();
  const m = LEDETEKST.exec(tekst);
  return { tokens: (m ? tekst.slice(m[0].length) : tekst).split(/\s+/).filter(Boolean), ledetekst: m !== null };
}

/** Innholdet i hver `[...]` på linjen. */
const hakerI = (linje: string): string[] => Array.from(linje.matchAll(I_HAKE), (m) => m[1]);

/** Linje med bare tall-akkorder og skilletegn, og minst én tall-akkord. Etter en ledetekst kreves minst to, eller én med `x<n>`. */
export function erTallakkordlinje(linje: string): boolean {
  const { tokens, ledetekst } = tokensUtenLedetekst(linje);
  const minst = ledetekst && !tokens.some((t) => GJENTAKELSE.test(t)) ? 2 : 1;
  return (
    tokens.length > 0 &&
    tokens.every((t) => erTallakkord(t) || SKILLETEGN.test(t)) &&
    tokens.filter(erTallakkord).length >= minst
  );
}

/** Antall bokstavakkorder på en linje der alle tokens er bokstavakkorder eller skilletegn (ellers 0). */
function bokstavakkorderILinje(linje: string): number {
  const { tokens } = tokensUtenLedetekst(linje);
  if (!tokens.every((t) => erBokstavakkord(t) || SKILLETEGN.test(t))) return 0;
  return tokens.filter(erBokstavakkord).length;
}

/** `[G]` eller `[Am F]`: innholdet er bare bokstavakkorder. */
function harBokstavakkordIHake(linje: string): boolean {
  return hakerI(linje).some((innhold) => {
    const tokens = innhold.trim().split(/\s+/).filter(Boolean);
    return tokens.length > 0 && tokens.every(erBokstavakkord);
  });
}

/**
 * Bokstavfil: filen har en `[G]` i tekst, en linje med minst 2 bokstavakkorder (og ellers bare skilletegn),
 * eller minst 2 linjer med én bokstavakkord. Meta- og tekstlinjer (`Key: G`, `A man`, `Am I`) utløser den aldri.
 */
export function erBokstavakkordFil(tekst: string): boolean {
  let enkle = 0;
  for (const linje of linjerI(tekst)) {
    if (harBokstavakkordIHake(linje)) return true;
    const antall = bokstavakkorderILinje(linje);
    if (antall >= 2) return true;
    if (antall === 1) enkle++;
  }
  return enkle >= 2;
}

/** Linje med minst 2 tall-akkorder teller som ett bevis, og hver `[tall]` teller som ett. */
function bevisILinje(linje: string): number {
  const antall = tokensUtenLedetekst(linje).tokens.filter(erTallakkord).length;
  return (erTallakkordlinje(linje) && antall >= 2 ? 1 : 0) + hakerI(linje).filter(erTallakkord).length;
}

/** Filregel: tall-fil bare hvis minst 3 bevis og ikke bokstavfil. */
export function harTallakkorder(tekst: string): boolean {
  if (erBokstavakkordFil(tekst)) return false;
  return linjerI(tekst).reduce((sum, linje) => sum + bevisILinje(linje), 0) >= MINSTE_BEVIS;
}
